<template>
  <fragment>
    <template v-if="user.id || societyId">
      <b-form-group
          horizontal
          invalid-feedback="This field is required."
          label="MembershipType"
      >
        <b-form-select
            v-model="$v.memberTypeMappingId.$model"
            :options="memberTypeMappingOptions"
            :state="validateState('memberTypeMappingId')"
        >
          <template slot="first">
            <option :value="null" disabled>
              -- Select --
            </option>
          </template>
        </b-form-select>
      </b-form-group>
      <b-form-group
          horizontal
          label="Year of birth"
          :invalid-feedback="yearInvalidFeedback"
      >
        <b-form-input
            placeholder="Year of birth"
            type="number"
            min="1900"
            :max="maxAllowedYear"
            v-model="$v.yearOfBirth.$model"
            :state="validateState('yearOfBirth')"
        />
      </b-form-group>
      <b-form-group
          horizontal
          :invalid-feedback="requiredInvalidFeedback"
          :label="this.isFieldRequiredByMembership('ethnicity') ? 'Ethnicity:' : 'Ethnicity (optional):'"
      >
        <b-form-select
            v-model="$v.ethnicity.$model"
            :options="$store.state.ethnicityOptions"
            :state="validateState('ethnicity')"
        />
      </b-form-group>
      <b-form-group
          horizontal
          :invalid-feedback="requiredInvalidFeedback"
          :label="this.isFieldRequiredByMembership('gender_identity') ? 'Gender' : 'Gender (optional):'"
      >
        <b-form-select
            v-model="$v.genderIdentity.$model"
            :options="$store.state.genderOptions"
            :state="validateState('genderIdentity')"
        />
      </b-form-group>
      <b-form-group label="U.S. Veteran" horizontal>
        <b-form-radio-group v-model="isUSVeteran" name="is_us_veteran">
          <b-form-radio :value="1">Yes</b-form-radio>
          <b-form-radio :value="0">No</b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <b-form-group
          horizontal
          :invalid-feedback="visaStatusInvalidFeedback"
          label="Visa status"
      >
        <b-form-select
            v-model="$v.visaStatus.$model"
            :options="visaStatusOptions"
            :state="validateState('visaStatus')"
        >
          <template slot="first">
            <option :value="null" disabled>
              -- Select --
            </option>
          </template>
        </b-form-select>
      </b-form-group>
      <b-form-group
          v-if="isFieldSupportedByMembership('practice_zip')"
          horizontal
          :invalid-feedback="zipInvalidFeedback"
          :label="this.isFieldRequiredByMembership('practice_zip') ? 'Practice ZIP Code:' : 'Practice ZIP Code (optional):'"
          class="zip-input"
      >
        <b-form-input
            placeholder="ZIP Code"
            type="number"
            min="0"
            max="99999"
            v-model="$v.zip.$model"
            :state="validateState('zip')"
        />
      </b-form-group>
      <b-form-group
          v-if="isFieldSupportedByMembership('npi')"
          horizontal
          :invalid-feedback="npiInvalidFeedback"
          :label="this.isFieldRequiredByMembership('npi') ? 'NPI:' : 'NPI (optional):'"
          class="npi-input"
      >
        <b-form-input
            placeholder="NPI"
            v-model="$v.npi.$model"
            :state="validateState('npi')"
        />
      </b-form-group>
      <b-form-group
          v-if="isFieldSupportedByMembership('specialty')"
          horizontal
          :invalid-feedback="requiredInvalidFeedback"
          label="Specialty"
      >
        <b-form-select
            v-model="$v.specialtyId.$model"
            :disabled="!societyId"
            :options="specialtyOptions"
            :state="validateState('specialtyId')"
        />
      </b-form-group>
      <b-form-group
          v-if="isFieldSupportedByMembership('subspecialty')"
          horizontal
          :invalid-feedback="requiredInvalidFeedback"
          label="Subspecialty"
      >
        <b-form-select
            v-model="$v.subspecialtyId.$model"
            :disabled="!specialtyId"
            :options="subspecialtyOptions"
            :state="validateState('subspecialtyId')"
        >
          <template slot="first">
            <option :value="null" disabled>
              -- Select --
            </option>
          </template>
        </b-form-select>
      </b-form-group>
      <b-form-group label="Email Settings" label-size="lg">
        <b-form-row>
          <b-col cols="12" md="6">
            <b-form-group horizontal label="Invalid">
              <b-form-checkbox
                  class="pt-2"
                  switch
                  v-model="$v.is_email_invalid.$model"
                  :value="1"
                  :unchecked-value="0"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group horizontal label="Opt-out">
              <b-form-checkbox
                  class="pt-2"
                  switch
                  v-model="$v.is_email_optout.$model"
                  :value="1"
                  :unchecked-value="0"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
      </b-form-group>
      <b-form-group
          v-if="isFieldSupportedByMembership('boardCertifications')"
          :state="validateState('boardCertifications')"
      >
        <user-profile-board-certification-list
            :certifications="boardCertifications"
            :excluded-certification-ids="excludedCertificationIds"
            :has-board-certification="hasBoardCertification"
            :has-board-certification-label="'User is currently either Board Certified or Board Eligible'"
            :society-id="parseInt(societyId)"
            @addBoardCertification="onAddBoardCertification"
            @update-has-board-certification="onUpdateHasBoardCertification"
        />
        <div
            v-show="$v.boardCertifications.$anyError"
            tabindex="-1"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            class="invalid-feedback"
        >
          {{ invalidFeedback.boardCertifications }}
        </div>
      </b-form-group>
    </template>
  </fragment>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  between,
  maxLength,
  minLength,
  numeric,
  required,
} from 'vuelidate/lib/validators';
import sortBy from 'lodash/sortBy';
import {SETTINGS_KEYS, VISA_STATUSES_LABELS} from '@/store';
import UserProfileBoardCertificationList from '@/components/users/profile/BoardCertification/UserProfileBoardCertificationList';
import GetMemberTypeMappingOptionsMixin from '@/mixins/GetMemberTypeMappingOptionsMixin';
import GetMembershipTypeOptionsMixin from '@/mixins/GetMembershipTypeOptionsMixin';
import EthnicityNaMixin from "@/mixins/EthnicityNaMixin";

const getSocietyMemberTypeMapping = (user, store) => {
  if (user.societyMemberTypeMapping) return user.societyMemberTypeMapping;
  return store.getters.getMemberTypeMapping(
      user.society_member_type_mapping_id
  );
};
export default {
  name: 'NonMssUserEditForm',
  mixins: [
    validationMixin,
    GetMemberTypeMappingOptionsMixin,
    GetMembershipTypeOptionsMixin,
    EthnicityNaMixin,
  ],
  components: {
    UserProfileBoardCertificationList,
  },
  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data: function () {
    let user = this.user || {};
    const societyMemberTypeMapping =
        getSocietyMemberTypeMapping(user, this.$store) || {};
    const nonSocietyId = this.$store.getters.getSetting(SETTINGS_KEYS.NON_SOCIETY_ID);
    return {
      societyId: nonSocietyId,
      specialtyId: user.specialty_id || null,
      subspecialtyId: user.subspecialty_id || null,
      yearOfBirth: user.year_of_birth || null,
      visaStatus: user.visa_status || null,
      ethnicity: user.ethnicity || null,
      genderIdentity: user.gender_identity || null,
      zip: user.practice_zip || '',
      hasBoardCertification: parseInt(user.has_board_certification) || 0,
      boardCertifications: user.boardCertifications
          ? [...user.boardCertifications]
          : [],
      isUSVeteran: user.is_us_veteran || 0,
      npi: user.npi || null,
      memberTypeMapping: user.societyMemberTypeMapping || null,
      memberTypeMappingId: user.society_member_type_mapping_id
          ? parseInt(user.society_member_type_mapping_id)
          : null,
      societyMembershipTypeId:
          societyMemberTypeMapping.society_membership_type_id || null,
      is_email_invalid: user.is_email_invalid || 0,
      is_email_optout: user.is_email_optout || 0,
      is_non_society: user.is_non_society || 0,
    };
  },
  validations: function () {
    let isSocietyRequired = !this.is_non_society ? required : '';
    const validations = {
      specialtyId: {},
      subspecialtyId: {},
      visaStatus: {
        required,
      },
      ethnicity: {},
      genderIdentity: {},
      societyId: {
        isSocietyRequired,
      },
      zip: {},
      yearOfBirth: {
        between: function (val) {
          return between(1900, this.maxAllowedYear)(val);
        },
      },
      npi: {},
      memberTypeMappingId: {
        required,
      },
      boardCertifications: {},
      is_email_invalid: {},
      is_email_optout: {},
    };
    if (
        this.isFieldSupportedByMembership('specialty') &&
        this.isFieldRequiredByMembership('specialty')
    ) {
      validations.specialtyId = {
        required,
      };
    }
    if (
        this.isFieldSupportedByMembership('subspecialty') &&
        this.isFieldRequiredByMembership('subspecialty')
    ) {
      validations.subspecialtyId = {
        required,
      };
    }
    if (this.isFieldSupportedByMembership('practice_zip')) {
      validations.zip = {
        minLength: minLength(5),
        maxLength: maxLength(5),
        numeric,
      };
      if (this.isFieldRequiredByMembership('practice_zip')) {
        validations.zip.required = required;
      }
    }
    if (this.isFieldSupportedByMembership('npi')) {
      validations.npi = {
        numeric,
        minLength: minLength(10),
        maxLength: maxLength(10),
      };
      if (this.isFieldRequiredByMembership('npi')) {
        validations.npi.required = required;
      }
    }
    if (
        this.hasBoardCertification &&
        this.isFieldSupportedByMembership('boardCertifications') &&
        this.isFieldRequiredByMembership('boardCertifications')
    ) {
      validations.boardCertifications = {
        required,
      };
    }
    return validations;
  },
  computed: {
    invalidFeedback: function () {
      return {
        zip: !this.$v.zip.numeric
            ? 'Must contain only numbers'
            : !this.$v.zip.minLength || !this.$v.zip.maxLength
                ? 'Zip code must be 5 characters long'
                : null,
        specialtyId: 'This field is required',
        subspecialtyId: 'This field is required',
        visaStatus: 'This field is required',
        boardCertifications: 'This field is required',
      };
    },

    maxAllowedYear: function () {
      return new Date().getFullYear() - 20;
    },
    specialtyOptions: function () {
      const defaultList = [{ value: null, text: 'Select specialty...' }];
      const society = (
          this.$store.state.societyOptions.find(
              (soc) => Number(soc.value) === Number(this.societyId)
          ) || {}
      ).society;
      if (!society) {
        return defaultList;
      }
      const sortedSpecialties = sortBy(society.specialties, (s) =>
          s.title.trim()
      );
      return [
        ...defaultList,
        ...sortedSpecialties.map((s) => ({
          value: s.id,
          text: s.title,
        })),
      ];
    },
    subspecialtyOptions: function () {
      if (!this.specialtyId) {
        return [];
      }
      return this.$store.state.subspecialtyOptions.filter(
          (subs) =>
              parseInt((subs.subspecialty || {}).specialty_id) ===
              parseInt(this.specialtyId)
      );
    },
    yearInvalidFeedback: function () {
      return `Year of birth must be between ${this.$v.yearOfBirth.$params.between.min} and ${this.$v.yearOfBirth.$params.between.max}`;
    },
    requiredInvalidFeedback: function () {
      return 'This field if required';
    },
    visaStatusInvalidFeedback: function () {
      return 'This field if required';
    },
    zipInvalidFeedback: function () {
      return !this.$v.zip.minLength || !this.$v.zip.maxLength
          ? 'Zip code should be 5 characters long'
          : !this.$v.zip.numeric
              ? 'Must contain only numbers'
              : 'This field is required';
    },
    npiInvalidFeedback: function () {
      return !this.$v.npi.numeric
          ? 'Must contain only numbers'
          : 'Npi should be 10 characters long';
    },
    visaStatusOptions: function () {
      return Object.keys(VISA_STATUSES_LABELS).map((key) => ({
        value: key,
        text: VISA_STATUSES_LABELS[key],
      }));
    },
    excludedCertificationIds: function () {
      return Array.isArray(this.boardCertifications)
          ? this.boardCertifications.map((bc) => parseInt(bc.id))
          : [];
    },
    membershipSupportedFields: function () {
      return this.$store.getters.getMembershipTypesSupportedFields(
          parseInt(this.societyMembershipTypeId)
      );
    },
    memberTypeMappingOptions: function () {
      const memberTypeMappings =
          this.$store.state.memberTypeMappings.items || [];
      return memberTypeMappings.reduce((options, memberTypeMapping) => {
        if (
            this.societyId &&
            parseInt(this.societyId) === parseInt(memberTypeMapping.society_id)
        ) {
          options.push({
            value: memberTypeMapping.id,
            text: memberTypeMapping.type_name,
            mapping: memberTypeMapping,
          });
        }
        return options;
      }, []);
    },
  },
  methods: {
    validateState: function (name) {
      if (!this.$v) return null;
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
    validate: function () {
      this.$v.$touch();
    },
    hasErrors: function () {
      return this.$v.$anyError;
    },
    getData: function () {
      let data = {
        society_id: this.societyId,
        ethnicity: this.ethnicity,
        gender_identity: this.genderIdentity,
        visa_status: this.visaStatus,
        is_us_veteran: this.isUSVeteran,
        year_of_birth: this.yearOfBirth,
        society_member_type_mapping_id: this.memberTypeMappingId,
        society_membership_type_id: this.societyMembershipTypeId,
        is_email_invalid: this.is_email_invalid,
        is_email_optout: this.is_email_optout
      };
      if (this.isFieldSupportedByMembership('specialty')) {
        data.specialty_id = this.specialtyId;
      }
      if (this.isFieldSupportedByMembership('subspecialty')) {
        data.subspecialty_id = this.subspecialtyId;
      }
      if (this.isFieldSupportedByMembership('boardCertifications')) {
        data.has_board_certification = this.hasBoardCertification;
        data.boardCertifications = this.boardCertifications;
      }
      if (this.isFieldSupportedByMembership('practice_zip')) {
        data.practice_zip = this.zip;
      }
      if (this.isFieldSupportedByMembership('npi')) {
        data.npi = this.npi;
      }
      return data;
    },
    onAddBoardCertification: function (certification) {
      this.boardCertifications.push(certification);
    },
    isFieldSupportedByMembership: function (field) {
      return (
          this.membershipSupportedFields &&
          Array.isArray(this.membershipSupportedFields.supported_fields) &&
          this.membershipSupportedFields.supported_fields.includes(field)
      );
    },
    isFieldRequiredByMembership: function (field) {
      return (
          this.membershipSupportedFields &&
          Array.isArray(this.membershipSupportedFields.profile_required_fields) &&
          this.membershipSupportedFields.profile_required_fields.includes(field)
      );
    },
    onUpdateHasBoardCertification: function (value) {
      if (!value) {
        this.boardCertifications = [];
      }
      this.hasBoardCertification = value;
    },
  },
  watch: {
    'user.society_id': function () {
      this.specialtyId = null;
    },
    'user.specialty_id': function () {
      this.subspecialtyId = null;
    },
    memberTypeMappingId: function (newMemberTypeMappingId) {
      this.memberTypeMapping = this.memberTypeMappingOptions.find(
          (mapping) => mapping.value === newMemberTypeMappingId
      ).mapping;
      this.societyMembershipTypeId = this.memberTypeMapping.society_membership_type_id;
    },
  },
};
</script>
